const duplicateSlashesRegex = /\/{2,}/g;
const whitespaceRegex = /\s/g;
/**
 * Clean a topic and remove any ambiguities based on the discussions here
 *  https://github.com/mqtt/mqtt.github.io/wiki/topic_format
 * without changing the meaning of the topic.
 *
 * @param {string} topic
 */
function cleanTopic(topic) {
	return topic
		.trim()
		.replace(duplicateSlashesRegex, '/')
		.replace(whitespaceRegex, '');
}

/** Regex that matches a # surrounded by / on both sides */
const inlineHash = /\/#\/+/g;
const startingHash = /^#\/?/;
/**
 * Join one or more subscription routes
 * @param  {...string} routes
 */
function joinRoutes(...routes) {
	return cleanTopic(
		routes
			.join('/')
			// remove in-topic occurrences of #.
			// This may occur if for instance we join foo/bar/# and x/y/z in that order
			.replace(inlineHash, '/')
			.replace(startingHash, '')
	);
}

/** Regex that matches most valid topics except one or two edge cases (false positives)
 *
 *          start of string,
 *
 *          (
 *
 *             # or + or (one or more ascii chars expect / $),
 *
 *              (forward slash and then one or more ascii chars except / $ ) <--- any number of times
 *
 *          ) may or may not be occur
 */
const semiValidRouteRegex = /^((\+|#|([a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"']+))(\/(([a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"'#+]+)|\+#))*)?$/;
const routeParamIncludingAnyLeadingSlashRegex = /(^|\/):[_a-zA-Z][_a-zA-Z0-9]*/g;

/** Check if a route is valid.
 *
 * The validity of a route is based on discussions here
 *  https://github.com/mqtt/mqtt.github.io/wiki/topic_format
 *  to reduce ambiguities
 *
 * - No leading slash; unless it is a single slash
 * - No trailing slash; unless it is a single slash
 * - No duplicate slashes
 * - No spaces
 * - Only ASCII characters
 * - Single occurrence of #, appearing only at the end
 *
 * Additionally, for the purposes of this lib,
 *
 * - No occurrences of $
 *
 * @param {string} route
 * */
function validateRoute(route) {
	if (!route.match(semiValidRouteRegex)) throw new Error(`Invalid route '${route}'`);

	const colonMatches = route.match(/:/g);
	const validRoutesMatches = route.match(routeParamIncludingAnyLeadingSlashRegex);
	if ((colonMatches || []).length !== (validRoutesMatches || []).length)
		throw Error(
			`
			A colon can only be used in a route param name.
			A route param must span an entire level, 
			and can only include alphanumeric characters or underscore
			but cannot start with a digit`
		);

	const hasAHash = route.match(/#/g);
	if (hasAHash && (!route.match(/#$/) || hasAHash.length > 1))
		throw new Error('A # can appear only once and must be at the end of the route');
}

/** Regex that matches a / followed by a #, or a # at the end of a string */
const slashHashOrHashRegex = /\/?#$/;
/**
 * Given a route, extract the part of the route that excludes the # wildcard if any.
 *
 * @param {string} route A valid route
 * @returns {string}
 */
function getParentRoute(route) {
	return route.split(slashHashOrHashRegex)[0];
}

/** Symbols that need to be escaped in a regex string */
const regexSymbolsRegex = /[.*?^${}()|[\]\\]/g;

/** Regex that represents MQTT wildcard +; any ascii character excluding +  #  /  and  $                              */
const singleTopicLevelRegex = /([a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"']+)/;

/** Regex that represents extended MQTT wildcard +; any ascii character including +  #  but excluding  /  $           */
const singleTopicLevelRegexExtended = /([a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"'+#]+)/;

/** Regex that represents MQTT wildcard /#; any ascii character excluding +  #  and  $                                */
const hashAfterASlashRegex = /((\/[a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"/']*)|$)/;

/** Regex that represents extended MQTT wildcard /#; any ascii character including +  #  but excluding  $             */
const hashAfterASlashRegexExtended = /((\/[a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"/'+#]*)|$)/;

/** Regex that represents MQTT wildcard #; any accepted ascii characters excluding +  #  and  $                       */
const standAloneHashRegex = /(([a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"/']*)|$)/;

/** Regex that represents extended MQTT wildcard #; any accepted ascii characters including +  #  but excluding  $    */
const standAloneHashRegexExtended = /(([a-zA-Z0-9_\-@()[\]{}|*!%&,.\\:;<=>?^`~"/'+#]*)|$)/;

/** Regex that matches a + followed directly by a / */
const plusAndThenHashRegex = /\+\//g;

const plusAtEndOfTextRegex = /\+$/;

/** Regex that matches parameter definitions in a route :param ;
 *  an underscore or alphabet, followed by an alphanumeric or underscore any number of times */
const routeParamsRegex = /:[_a-zA-Z][_a-zA-Z0-9]*/g;

/** Convert a route to a regular expression
 * @param {string} route An already cleaned topic with/without params and with/without mqtt wildcards.
 *  See `cleanTopic()` for the definition of a clean topic
 * @param {boolean} shouldMatchEntireString If true, the returned regex will match an entire topic string.
 *  Else it will match part of a topic string starting from the beginning of the string
 * */
function convertRouteToRegex(route, shouldMatchEntireString = true) {
	// escape regex symbols
	let topicAsRegex = route.replace(regexSymbolsRegex, '\\$&'); // $& means the whole matched string

	// replace single level mqtt wildcard +, with a regex
	// when the + wildcard is followed by a slash
	topicAsRegex = topicAsRegex.replace(plusAndThenHashRegex, `${singleTopicLevelRegex.source}/`);
	// when the + wildcard occurs at the end of the topic; This can only appear once in a topic
	topicAsRegex = topicAsRegex.replace(
		plusAtEndOfTextRegex,
		`${singleTopicLevelRegex.source}${shouldMatchEntireString ? '$' : ''}`
	);

	// replace multi-level mqtt wildcard #, with a regex
	// A # can only appear once in a topic, alone or at the end of the topic
	topicAsRegex = topicAsRegex.replace(/\/#/, hashAfterASlashRegex.source);
	topicAsRegex = topicAsRegex.replace(/#/, standAloneHashRegex.source);

	// replace param names with regex named capture groups.
	// Every valid param represents a single level wildcard in the topic,
	const paramsDefinitionMatches = route.match(routeParamsRegex);
	if (paramsDefinitionMatches) {
		paramsDefinitionMatches.forEach(paramDef => {
			topicAsRegex = topicAsRegex.replace(
				paramDef,
				// Note that params can only span a single level in a topic
				// The name of the capture group, is the name of the corresponding param
				`(?<${paramDef.replace(':', '')}>${singleTopicLevelRegex.source})`
			);
		});
	}

	// the regex must match an entire text, not a part of it
	topicAsRegex = `^${topicAsRegex}`;
	if (shouldMatchEntireString && !topicAsRegex.endsWith('$') && !topicAsRegex.endsWith('$)'))
		topicAsRegex = `${topicAsRegex}$`;

	return new RegExp(topicAsRegex);
}

function convertMqttTopicToRegex(mqttTopic, shouldMatchEntireString = true) {
	// escape regex symbols
	let topicAsRegex = mqttTopic.replace(regexSymbolsRegex, '\\$&'); // $& means the whole matched string

	// replace single level mqtt wildcard +, with a regex
	// when the + wildcard is followed by a slash
	topicAsRegex = topicAsRegex.replace(
		plusAndThenHashRegex,
		`${singleTopicLevelRegexExtended.source}/`
	);
	// when the + wildcard occurs at the end of the topic; This can only appear once in a topic
	topicAsRegex = topicAsRegex.replace(
		plusAtEndOfTextRegex,
		`${singleTopicLevelRegexExtended.source}${shouldMatchEntireString ? '$' : ''}`
	);

	// replace multi-level mqtt wildcard #, with a regex
	// A # can only appear once in a topic, alone or at the end of the topic
	topicAsRegex = topicAsRegex.replace(/\/#$/, hashAfterASlashRegexExtended.source);
	topicAsRegex = topicAsRegex.replace(/^#$/, standAloneHashRegexExtended.source);

	// the regex must match an entire text, not a part of it
	topicAsRegex = `^${topicAsRegex}`;
	if (shouldMatchEntireString && !topicAsRegex.endsWith('$') && !topicAsRegex.endsWith('$)'))
		topicAsRegex = `${topicAsRegex}$`;

	return new RegExp(topicAsRegex);
}

/**
 * @param {string} route
 * @param {string} sharedGroup
 */
function convertRouteToMqttTopic(route, sharedGroup = null) {
	// every param represents a single level wildcard in the topic,
	//  no matter it's position in the topic string
	let topic = route.replace(routeParamsRegex, '+');
	if (sharedGroup) topic = joinRoutes('$share', sharedGroup, topic);
	return topic;
}

module.exports = {
	cleanTopic,
	joinRoutes,
	validateRoute,
	convertRouteToMqttTopic,
	convertRouteToRegex,
	convertMqttTopicToRegex,
	getParentRoute,
};
